'use client';

import { BookCard, EmptyState, SectionHeading, Spinner } from '@lectura/ui';
import { BookOpen, CheckCircle2, Bookmark as BookmarkIcon } from 'lucide-react';
import Link from 'next/link';
import { useMemo } from 'react';
import { SiteShell } from '@/components/SiteShell';
import { useAuth } from '@/lib/auth';
import { useAllBookmarks } from '@/lib/bookmarks';
import { usePublication, useReadingHistory } from '@/lib/queries';
import { useHydrated } from '@/lib/use-hydrated';

const FINISHED_THRESHOLD = 100;

/**
 * Polcom oldal — bejelentkezett felhasználó saját könyvtárgyűjteménye.
 *
 * Három szekció:
 * 1. **Folyamatban olvasott** — `useReadingHistory` rekordjai 100% alatt,
 *    csökkenő frissesség szerint.
 * 2. **Befejezett** — 100%-os rekordok.
 * 3. **Könyvjelzőzött** — `useAllBookmarks` egyedi publikáció-azonosítói.
 *
 * Bejelentkezetlen felhasználónál nincs adat — egy CTA visszairányít a
 * loginra. Mock-fallback NINCS (lásd `feedback_no_mocks.md`).
 */
export default function ShelfPage() {
  // SSR-en a Zustand-Persist nincs hidratálva — első renderben mindig
  // bejelentkezetlen-UI-t mutatunk, és csak hidratálás után váltunk a valós
  // auth-állapotra. Ezzel kerüljük el a Hydration mismatch-et.
  const hydrated = useHydrated();
  const isLogged = useAuth((s) => s.isAuthenticated());
  const history = useReadingHistory();
  const allBookmarks = useAllBookmarks();

  const { inProgress, finished, bookmarkedIds } = useMemo(() => {
    const records = history.data ?? [];
    const inProgress = records.filter((r) => r.percent < FINISHED_THRESHOLD);
    const finished = records.filter((r) => r.percent >= FINISHED_THRESHOLD);
    const bookmarkedIds = Array.from(
      new Set((allBookmarks.data ?? []).map((b) => b.publicationId)),
    );
    return { inProgress, finished, bookmarkedIds };
  }, [history.data, allBookmarks.data]);

  if (!hydrated || !isLogged) {
    return (
      <SiteShell>
        <div className="mx-auto max-w-2xl px-4 py-16 text-center sm:px-6">
          <BookOpen className="mx-auto size-10 text-[var(--color-foreground-muted)]" />
          <h1 className="mt-4 font-display text-2xl font-bold">A Polcod ide kerül</h1>
          <p className="mt-2 text-[var(--color-foreground-muted)]">
            Lépj be, és minden eszközödön egységesen láthatod a folyamatban lévő
            olvasmányaidat, könyvjelzőidet és kiemeléseidet.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Link
              href="/login"
              className="rounded-full bg-brand-600 px-6 py-2 text-sm font-semibold text-white hover:bg-brand-700"
            >
              Belépés
            </Link>
            <Link
              href="/signup"
              className="rounded-full border border-[var(--color-border)] px-6 py-2 text-sm font-semibold hover:bg-[var(--color-surface-subtle)]"
            >
              Regisztráció
            </Link>
          </div>
        </div>
      </SiteShell>
    );
  }

  const isLoading = history.isLoading || allBookmarks.isLoading;

  return (
    <SiteShell>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Polcom"
          title="A te olvasmányaid"
          description="Folyamatban lévő, befejezett és könyvjelzőzött kiadványok — eszközök között szinkronizálva."
        />

        {isLoading ? (
          <div className="flex justify-center py-20">
            <Spinner className="size-8" />
          </div>
        ) : (
          <div className="space-y-12">
            <ShelfSection
              icon={<BookOpen />}
              title="Folyamatban"
              count={inProgress.length}
              empty="Nincs folyamatban lévő olvasmány. Nyiss meg egy könyvet a könyvtárból."
            >
              {inProgress.map((p) => (
                <ShelfCard key={p.publicationId} publicationId={p.publicationId} percent={p.percent} />
              ))}
            </ShelfSection>

            <ShelfSection
              icon={<CheckCircle2 />}
              title="Befejezett"
              count={finished.length}
              empty="Még nincs befejezett könyved — érd el a 100%-ot, és ide kerül."
            >
              {finished.map((p) => (
                <ShelfCard key={p.publicationId} publicationId={p.publicationId} percent={p.percent} />
              ))}
            </ShelfSection>

            <ShelfSection
              icon={<BookmarkIcon />}
              title="Könyvjelzőzve"
              count={bookmarkedIds.length}
              empty="Még nem mentettél könyvjelzőt. Az olvasónézet felső sávján a könyvjelző ikonnal jelölheted ki a fontos szakaszokat."
            >
              {bookmarkedIds.map((id) => (
                <ShelfCard key={id} publicationId={id} />
              ))}
            </ShelfSection>
          </div>
        )}
      </div>
    </SiteShell>
  );
}

interface ShelfSectionProps {
  icon: React.ReactNode;
  title: string;
  count: number;
  empty: string;
  children: React.ReactNode;
}

function ShelfSection({ icon, title, count, empty, children }: ShelfSectionProps) {
  return (
    <section>
      <div className="mb-4 flex items-center gap-2">
        <span className="flex size-7 items-center justify-center rounded-full bg-brand-100 text-brand-700 [&>svg]:size-4">
          {icon}
        </span>
        <h2 className="font-display text-lg font-semibold">{title}</h2>
        <span className="text-sm text-[var(--color-foreground-muted)]">({count})</span>
      </div>
      {count === 0 ? (
        <EmptyState icon={icon as React.ReactElement} title="" description={empty} />
      ) : (
        <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
          {children}
        </div>
      )}
    </section>
  );
}

interface ShelfCardProps {
  publicationId: string;
  /** Olvasási % (0..100) — a kártya alján mini progress-csíkként látszik. */
  percent?: number;
}

function ShelfCard({ publicationId, percent }: ShelfCardProps) {
  // Egyenként húzzuk fel a publikáció-metaadatot a catalog-svc-ből. Egy /shelf-en
  // tipikusan 5–30 publikáció — ez N külön kérés, de a TanStack Query
  // dedupálja és cache-eli, így újranyitásnál ingyenes.
  const pub = usePublication(publicationId);
  if (pub.isLoading) {
    return (
      <div className="flex aspect-[2/3] items-center justify-center rounded-2xl bg-[var(--color-surface-subtle)]">
        <Spinner className="size-5" />
      </div>
    );
  }
  if (pub.isError || !pub.data) {
    return (
      <div className="flex aspect-[2/3] items-center justify-center rounded-2xl border border-dashed border-[var(--color-border)] p-3 text-center text-xs text-[var(--color-foreground-muted)]">
        Publikáció nem érhető el
      </div>
    );
  }
  return (
    <Link href={`/read/${pub.data.id}` as const} aria-label={pub.data.title}>
      <div className="space-y-2">
        <BookCard
          title={pub.data.title}
          authors={pub.data.contributors.filter((c) => c.role === 'author').map((c) => c.name)}
          coverUrl={pub.data.coverUrl ?? undefined}
        />
        {typeof percent === 'number' ? (
          <div
            className="h-1 w-full overflow-hidden rounded-full bg-brand-100"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            aria-label={`${pub.data.title} olvasási haladás`}
          >
            <div
              className="h-full rounded-full bg-gradient-to-r from-brand-500 to-brand-700"
              style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
            />
          </div>
        ) : null}
      </div>
    </Link>
  );
}
